/**
 * Blackout Contracts — L3 optional challenge raids (M3 S6).
 *
 * Two halves that must not be confused:
 *
 *   ARMED   — `gameState.currentRaid.blackoutContract`, the contract this raid
 *             is running under. Read for MODIFIERS, and only for the raid in
 *             progress.
 *   CLEARED — `gameState.syndicate.blackout.cleared`, the account's permanent
 *             record. Read for PERKS, forever, whatever raid is running.
 *
 * The perks are permanent and each contract pays once, so this is a finite
 * hand-authored progression rather than a grind. That is what lets the perks be
 * flat and generous, and it is why `getBlackoutPerk` sums over cleared contracts
 * rather than anything time-bound.
 *
 * No browser dependencies — the simulator runs this too.
 *
 * See docs/layers/3-syndicate/design.md §7.
 */

import * as config from '../constants.js';
import { gameState } from './gameState-core.js';
import { getCharterRank, ensureSyndicateState } from './syndicate-core.js';

/** Contracts are only offered once the charter is signed. */
export function isBlackoutUnlocked() {
    return getCharterRank('blackout-sanction') > 0;
}

export function getBlackoutContract(contractId) {
    return config.blackoutContracts?.[contractId] || null;
}

export function blackoutContractIds() {
    return Object.keys(config.blackoutContracts || {});
}

/** Has this contract already paid out? Perks are one-time and permanent. */
export function isBlackoutCleared(contractId) {
    return !!ensureSyndicateState().blackout?.cleared?.[contractId];
}

/**
 * Why this contract cannot be armed at this tier, or null.
 *
 * A reason rather than a boolean: "not available" with no explanation reads as
 * a bug, and the two causes (already cleared, tier too low) want different
 * copy — one is finished, the other is a goal.
 */
export function blackoutBlockedReason(contractId, tier) {
    if (!isBlackoutUnlocked()) return 'no-sanction';
    const contract = getBlackoutContract(contractId);
    if (!contract) return 'unknown-contract';
    if (isBlackoutCleared(contractId)) return 'already-cleared';
    if ((tier || 0) < (contract.minTier || 1)) return 'tier-too-low';
    return null;
}

/** Contracts that can be armed at `tier` right now. */
export function availableBlackouts(tier) {
    if (!isBlackoutUnlocked()) return [];
    return blackoutContractIds()
        .filter(id => !blackoutBlockedReason(id, tier))
        .map(id => ({ id, ...getBlackoutContract(id) }));
}

// ---------------------------------------------------------------------------
// Arming — the raid currently being planned or run
// ---------------------------------------------------------------------------

/** The contract this raid is running under, or null. */
export function activeBlackout() {
    const id = gameState.currentRaid?.blackoutContract;
    if (!id) return null;
    const contract = getBlackoutContract(id);
    return contract ? { id, ...contract } : null;
}

/**
 * Arm or disarm a contract for the raid being planned.
 *
 * Refuses once a raid is under way: the modifiers shape how the raid is set up
 * (crew, consumables, starting HP), so arming one mid-raid would grant a perk
 * for conditions that were never actually run.
 */
export function setBlackoutContract(contractId, tier = gameState.currentRaid?.selectedTier) {
    if (!gameState.currentRaid) return { ok: false, reason: 'no-raid-state' };
    if (gameState.currentRaid.raidInProgress) return { ok: false, reason: 'raid-in-progress' };

    if (!contractId) {
        gameState.currentRaid.blackoutContract = null;
        return { ok: true, contract: null };
    }
    const reason = blackoutBlockedReason(contractId, tier);
    if (reason) return { ok: false, reason };

    gameState.currentRaid.blackoutContract = contractId;
    return { ok: true, contract: { id: contractId, ...getBlackoutContract(contractId) } };
}

/**
 * A modifier off the ARMED contract, or `fallback`.
 *
 * Every integration point calls this rather than reading the contract directly,
 * so a raid with nothing armed answers the same shape as one that has — the
 * decision points stay branch-free.
 */
export function blackoutModifier(key, fallback = undefined) {
    const contract = activeBlackout();
    if (!contract) return fallback;
    const value = contract.modifiers?.[key];
    return value === undefined ? fallback : value;
}

// ---------------------------------------------------------------------------
// Clearing and perks
// ---------------------------------------------------------------------------

/**
 * Record a successful Blackout and disarm it.
 *
 * ONLY on success. A failed or abandoned Blackout pays nothing — the modifiers
 * are the price of the perk, and clearing on any completion would let a player
 * arm the hardest contract and walk into the first hazard.
 *
 * @returns {{contractId:string, perk:object}|null} what was earned, for the UI
 *   to announce, or null when there was nothing armed.
 */
export function clearActiveBlackout({ success }) {
    const contract = activeBlackout();
    if (!contract) return null;

    gameState.currentRaid.blackoutContract = null;
    if (!success) return null;

    const syndicate = ensureSyndicateState();
    if (!syndicate.blackout) syndicate.blackout = { cleared: {} };
    if (!syndicate.blackout.cleared) syndicate.blackout.cleared = {};
    // First clear only: re-running a cleared contract must not double the perk.
    if (syndicate.blackout.cleared[contract.id]) return null;

    syndicate.blackout.cleared[contract.id] = true;
    return { contractId: contract.id, perk: contract.perk };
}

/**
 * The total perk value earned for an effect key, across every cleared contract.
 *
 * Mirrors `getUpgradeEffect` so integration sites read alike, with one
 * difference that matters: upgrades have ranks and return the value AT a rank,
 * while contracts are one-shot and SUM. Written as a sum rather than a lookup so
 * a future contract can stack onto an existing key without touching callers.
 */
export function getBlackoutPerk(effectKey, fallback = 0) {
    const cleared = ensureSyndicateState().blackout?.cleared || {};
    let total = 0;
    let found = false;
    for (const contractId of Object.keys(cleared)) {
        if (!cleared[contractId]) continue;
        const perk = getBlackoutContract(contractId)?.perk;
        if (perk?.key === effectKey) { total += perk.value; found = true; }
    }
    return found ? total : fallback;
}

/** Every earned perk, for the Syndicate screen's summary. */
export function earnedBlackoutPerks() {
    const cleared = ensureSyndicateState().blackout?.cleared || {};
    return blackoutContractIds()
        .filter(id => cleared[id])
        .map(id => ({ id, ...getBlackoutContract(id) }));
}
